import React, { useState, useRef } from 'react';
import {
  Box,
  Avatar,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { PhotoCamera, Delete } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { useProfileImage, useGenerateUploadUrl, useUpdateProfileImage, useDeleteProfileImage } from '@/api/profile';
import { useAuth } from '@/contexts/AuthContext';

interface AvatarUploadProps {
  size?: number;
  showDelete?: boolean;
  onImageChange?: (imageUrl: string | null) => void;
  initials?: string;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({ 
  size = 120, 
  showDelete = true,
  onImageChange,
  initials = 'U'
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // API hooks
  const { user } = useAuth();
  const { data: profileData, refetch: refetchProfile } = useProfileImage({
    enabled: !!user?.id,
  });
  const generateUploadUrlMutation = useGenerateUploadUrl();
  const updateProfileImageMutation = useUpdateProfileImage();
  const deleteProfileImageMutation = useDeleteProfileImage();

  const currentImageUrl = previewUrl || profileData?.profileImageUrl;

  const resizeImage = (file: File, maxWidth: number = 256, maxHeight: number = 256): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      img.onload = () => {
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and resize image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to resize image'));
              return;
            }

            // Create new file with resized blob
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });

            resolve(resizedFile);
          },
          file.type,
          0.9 // Quality for JPEG
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const resizeAndUploadFile = async (file: File) => {
    try {
      // Create preview first
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Resize the image
      const resizedFile = await resizeImage(file, 256, 256);
      
      // Upload the resized file
      uploadFile(resizedFile);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to process image', { variant: 'error' });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar('Invalid file type. Please select a JPEG, PNG, GIF, or WebP image.', { variant: 'error' });
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      enqueueSnackbar('File size too large. Please select an image smaller than 5MB.', { variant: 'error' });
      return;
    }

    // Resize and upload the file
    resizeAndUploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    
    try {
      // Get file extension and content type
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const contentType = file.type;

      // Step 1: Get presigned upload URL from our server
      const uploadData = await generateUploadUrlMutation.mutateAsync({
        fileExtension,
        contentType,
      });

      // Step 2: Upload directly to AWS S3 (not our server)
      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': contentType,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload to S3');
      }

      // Step 3: Tell our server about the successful upload
      const updateData = await updateProfileImageMutation.mutateAsync({
        s3Key: uploadData.s3Key,
      });

      // Clear preview and refresh profile data
      setPreviewUrl(null);
      await refetchProfile();

      queryClient.setQueryData(['currentUser'], (prev: Record<string, unknown> | null | undefined) =>
        prev && typeof prev === 'object' ? { ...prev, profileImageUrl: updateData.profileImageUrl } : prev,
      );
      
      // Notify parent component
      onImageChange?.(updateData.profileImageUrl);
      
      enqueueSnackbar('Profile image updated successfully!', { variant: 'success' });
    } catch (error: any) {
      console.error('Upload failed:', error);
      enqueueSnackbar(error.message || 'Failed to upload image', { variant: 'error' });
      setPreviewUrl(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProfileImageMutation.mutateAsync();
      await refetchProfile();
      queryClient.setQueryData(['currentUser'], (prev: Record<string, unknown> | null | undefined) =>
        prev && typeof prev === 'object' ? { ...prev, profileImageUrl: null } : prev,
      );
      onImageChange?.(null);
      enqueueSnackbar('Profile image deleted successfully!', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to delete image', { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar
          src={currentImageUrl || undefined}
          sx={{
            width: size,
            height: size,
            cursor: 'pointer',
            border: '2px solid',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
            },
          }}
          onClick={handleAvatarClick}
        >
          {!currentImageUrl && initials}
        </Avatar>
        
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '50%',
            }}
          >
            <CircularProgress size={32} sx={{ color: 'white' }} />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<PhotoCamera />}
          onClick={handleAvatarClick}
          disabled={isUploading}
          size="small"
        >
          {currentImageUrl ? 'Change' : 'Upload'}
        </Button>
        
        {showDelete && currentImageUrl && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setDeleteDialogOpen(true)}
            disabled={isUploading}
            size="small"
          >
            Delete
          </Button>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" align="center">
        Click to upload a profile image
        <br />
        Max size: 5MB • Auto-resized to 256x256 • Supported: JPEG, PNG, GIF, WebP
      </Typography>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Profile Image</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your profile image? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            disabled={deleteProfileImageMutation.isPending}
          >
            {deleteProfileImageMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

